import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  approveCatalogoSugestao,
  createCatalogoSugestao,
  fetchCatalogoSugestoes,
  fetchMunicipios,
  rejectCatalogoSugestao,
  type CatalogoSugestao,
  type Municipio,
} from '../services/api';
import { useT } from '../i18n';
// Issue #550: Page shell — padroniza o header da pagina (h1 + subtitulo).
import Page from '../components/Page';

// Decisao de produto (substitui a visao de catalogo p/ orgs, #559):
// a pagina "Solicitar provedor" e o ponto de contato dos usuarios de org
// com o catalogo colaborativo — aviso com os provedores suportados hoje,
// formulario de sugestao e as solicitacoes da propria org (o backend ja
// filtra por organization_id para nao-moderadores).
// Para parceiro/super_user (CATALOG_MODERATION_ROLES do backend,
// catalogo.py) a secao de solicitacoes vira a fila de moderacao com as
// sugestoes PENDENTES de todas as orgs + acoes aprovar/rejeitar — assim
// o parceiro mantem a moderacao mesmo sem acesso ao catalogo completo.
const SUGESTAO_MODERATION_ROLES = ['super_user', 'parceiro'];

interface Props {
  userRole: string;
}

const EMPTY_FORM = {
  codigo_ibge: '',
  nome: '',
  uf: '',
  provedor: '',
  agregador: '',
  justificativa: '',
};

export default function SolicitarProvedor({ userRole }: Props) {
  const t = useT();
  // Moderador (parceiro/super_user): ve a fila de sugestoes pendentes de
  // todas as orgs com acoes; demais roles veem "Minhas solicitacoes".
  const canModerate = SUGESTAO_MODERATION_ROLES.includes(userRole);

  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [sugestoes, setSugestoes] = useState<CatalogoSugestao[]>([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const [m, sug] = await Promise.all([
        fetchMunicipios(),
        // Fila de moderacao = apenas pendentes (backend ja filtra por role;
        // nao-moderadores recebem so as sugestoes da propria org).
        canModerate ? fetchCatalogoSugestoes('pendente') : fetchCatalogoSugestoes(),
      ]);
      setMunicipios(m);
      setSugestoes(sug);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    } finally {
      setLoading(false);
    }
  }, [canModerate, t]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Aviso "provedores suportados hoje": lista derivada do catalogo —
  // agrupa municipios por provedor (nome provedor + municipios/UF),
  // formato simples (nao e a tabela do catalogo).
  const providers = useMemo(() => {
    const map = new Map<string, Municipio[]>();
    for (const m of municipios) {
      const list = map.get(m.provedor) ?? [];
      list.push(m);
      map.set(m.provedor, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [municipios]);

  const submitSugestao = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      await createCatalogoSugestao({
        codigo_ibge: form.codigo_ibge.trim(),
        nome: form.nome.trim(),
        uf: form.uf.trim(),
        provedor: form.provedor.trim(),
        agregador: form.agregador.trim() || null,
        justificativa: form.justificativa.trim() || null,
      });
      setForm(EMPTY_FORM);
      setSuccess(t('catalogo.suggest.success'));
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    }
  };

  const handleApprove = async (id: string) => {
    setError(null);
    try {
      await approveCatalogoSugestao(id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    }
  };

  const handleReject = async (id: string) => {
    const note = window.prompt(t('catalogo.moderation.rejectPrompt')) ?? undefined;
    setError(null);
    try {
      await rejectCatalogoSugestao(id, note);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('catalogo.error.generic'));
    }
  };

  if (loading) return <p className="text-brand-600">{t('app.loading')}</p>;

  const inputCls =
    'rounded border border-brand-300 px-2 py-1 text-sm text-brand-800 focus:border-brand-500 focus:outline-none';
  const thCls = 'py-2 pr-4 text-left text-xs font-semibold text-brand-500';
  const tdCls = 'py-2 pr-4 text-sm text-brand-800';

  return (
    <Page title={t('catalogo.request.title')} subtitle={t('catalogo.request.subtitle')}>
      {/* Issue #569: linha simples de texto com os nomes dos provedores
          suportados (únicos, ordenados) — logo após o subtítulo, ADICIONAL
          ao aviso agrupado abaixo. Oculta quando o catálogo está vazio
          (a seção "Suportados" já comunica o estado vazio). */}
      {providers.length > 0 && (
        <p className="text-sm text-brand-600">
          {t('catalogo.supported.line', {
            list: providers.map(([provedor]) => provedor).join(', '),
          })}
        </p>
      )}
      {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && <p className="rounded bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>}

      {/* Aviso: provedores suportados hoje (leitura do catalogo) */}
      <section className="rounded-lg border border-brand-200 bg-white p-6">
        <h2 className="mb-2 text-lg font-semibold text-brand-800">
          {t('catalogo.supported.title')}
        </h2>
        <p className="mb-3 text-sm text-brand-600">{t('catalogo.supported.notice')}</p>
        {providers.length === 0 ? (
          <p className="text-sm text-brand-500">{t('catalogo.table.empty')}</p>
        ) : (
          <ul className="list-disc space-y-1 pl-5 text-sm text-brand-800">
            {providers.map(([provedor, list]) => (
              <li key={provedor}>
                <span className="font-medium">{provedor}</span>
                {' — '}
                {list.map((m) => `${m.nome} (${m.uf})`).join(', ')}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Formulario "Solicitar provedor" — mesmo submitSugestao/endpoint
          do fluxo de sugestoes do catalogo. */}
      <section className="rounded-lg border border-brand-200 bg-white p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-800">
          {t('catalogo.request.title')}
        </h2>
        <form onSubmit={submitSugestao} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.ibge')}
            <input
              required
              minLength={7}
              maxLength={7}
              className={inputCls}
              value={form.codigo_ibge}
              onChange={(e) => setForm({ ...form, codigo_ibge: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.nome')}
            <input
              required
              className={inputCls}
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.uf')}
            <input
              required
              minLength={2}
              maxLength={2}
              className={inputCls}
              value={form.uf}
              onChange={(e) => setForm({ ...form, uf: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.provedor')}
            <input
              required
              className={inputCls}
              value={form.provedor}
              onChange={(e) => setForm({ ...form, provedor: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.agregador')}
            <input
              className={inputCls}
              value={form.agregador}
              onChange={(e) => setForm({ ...form, agregador: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-brand-500">
            {t('catalogo.suggest.justificativa')}
            <input
              className={inputCls}
              value={form.justificativa}
              onChange={(e) => setForm({ ...form, justificativa: e.target.value })}
            />
          </label>
          <div className="sm:col-span-3">
            <button
              type="submit"
              className="rounded bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700"
            >
              {t('catalogo.request.submit')}
            </button>
          </div>
        </form>
      </section>

      {/* Solicitacoes — roles de org veem "Minhas solicitacoes" (o backend
          retorna apenas as da propria org, com review_note da moderacao);
          parceiro/super_user veem a fila de moderacao (pendentes de todas
          as orgs) com acoes aprovar/rejeitar. */}
      <section className="rounded-lg border border-brand-200 bg-white p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-800">
          {canModerate ? t('catalogo.moderation.queue') : t('catalogo.sugestoes.mine')}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-brand-100">
                <th className={thCls}>{t('catalogo.table.ibge')}</th>
                <th className={thCls}>{t('catalogo.table.nome')}</th>
                <th className={thCls}>{t('catalogo.table.uf')}</th>
                <th className={thCls}>{t('catalogo.table.provedor')}</th>
                {canModerate ? (
                  <>
                    <th className={thCls}>{t('catalogo.table.justificativa')}</th>
                    <th className={thCls}>{t('catalogo.moderation.actions')}</th>
                  </>
                ) : (
                  <th className={thCls}>{t('common.status')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {sugestoes.map((s) => (
                <tr key={s.id} className="border-b border-brand-50">
                  <td className={tdCls}>{s.codigo_ibge}</td>
                  <td className={tdCls}>{s.nome}</td>
                  <td className={tdCls}>{s.uf}</td>
                  <td className={tdCls}>{s.provedor}</td>
                  {canModerate ? (
                    <>
                      <td className={tdCls}>{s.justificativa ?? t('common.dash')}</td>
                      <td className={tdCls}>
                        <span className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleApprove(s.id)}
                            className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700"
                          >
                            {t('catalogo.moderation.approve')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReject(s.id)}
                            className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                          >
                            {t('catalogo.moderation.reject')}
                          </button>
                        </span>
                      </td>
                    </>
                  ) : (
                    <td className={tdCls}>
                      {t(`catalogo.sugestoes.status.${s.status}`)}
                      {/* A nota da moderacao (ex.: motivo da rejeicao)
                          aparece junto ao status. */}
                      {s.review_note && (
                        <p className="mt-0.5 text-xs text-brand-400">{s.review_note}</p>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {sugestoes.length === 0 && (
                <tr>
                  <td className={tdCls} colSpan={canModerate ? 6 : 5}>
                    {canModerate
                      ? t('catalogo.moderation.queueEmpty')
                      : t('catalogo.sugestoes.mineEmpty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </Page>
  );
}
